import React from "react"
import Image from "next/image"
import { Slot } from "@radix-ui/react-slot"

import { IKTRichText, TResolvedImage } from "@/types/kontent.types"
import { sanitizeHtml } from "@/lib/sanitizeHtml"
import { cn, parseSimpleString } from "@/lib/utils"
import { RichTextSync, Typography } from "@/components/ui/atoms"

export interface IHeroSectionProps {
  asChild?: boolean
  title: string
  subtitle?: string
  body?: IKTRichText
  widgetId?: string
  image?: TResolvedImage | null
  isHomepage?: boolean
}

const HeroSection = React.forwardRef<HTMLDivElement, IHeroSectionProps>(
  (
    {
      asChild = false,
      widgetId,
      title,
      image,
      body,
      subtitle,
      isHomepage,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : "div"

    return (
      <Component
        className={cn(
          "hero-section relative",
          isHomepage ? " bg-neutral-200" : " bg-secondary-500"
        )}
        ref={ref}
        {...props}
      >
        <div className="items-center lg:flex">
          {image && image.width && image.height ? (
            <div
              className={cn(
                "hero-section--img relative min-h-[225px] self-stretch lg:order-2 lg:w-6/12",
                isHomepage ? "lg:min-h-[800px]" : "lg:min-h-[500px]"
              )}
            >
              <Image
                src={image.src}
                alt={image.alt ?? ""}
                fill
                priority
                sizes="(min-width: 1024px) 50vw, 100vw"
                className="object-cover"
              />
            </div>
          ) : null}
          <div className="lg:order-1 lg:w-6/12">
            <div className="mx-auto px-5 sm:max-w-xl md:max-w-3xl lg:mr-0 lg:max-w-lg lg:px-0 xl:max-w-[40rem] 2xl:max-w-screen-md">
              <div className="py-8 lg:pl-6 lg:pr-12">
                <div
                  className="heading-xl heading--bitypo mb-9"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(title) }}
                />
                {subtitle ? (
                  <Typography className="mb-9" as="h2" variant="paragraphL">
                    {subtitle}
                  </Typography>
                ) : null}
                {body ? (
                  <RichTextSync
                    blockTitle={parseSimpleString(title)}
                    body={body}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </Component>
    )
  }
)
HeroSection.displayName = "HeroSection"

export { HeroSection }
